import { getConfig } from '@local/app-config';
import type { JSX } from 'react';
import { Component } from 'react';

import { initializeErrorLogging, logUnknownException } from './errorLogging';
import type { ErrorLoggingConfiguration, BeforeSendType } from './errorLogging.types';
import { Severity } from './errorLogging.types';

export interface ErrorBoundaryProps {
    children: JSX.Element;
    /**
     * Component to render when an error is caught. Can be a JSX element or a function that receives the error ID that was logged by the error logging service.
     *
     * Note that when an exception is caught, the error component may be rendered before the error ID is available. If a function is provided, it should be able to handle the case where the error ID is not yet defined.
     * This is because when an error is caught, we want to ensure the error component is rendered as soon as possible, even if the error ID has not yet been generated by the error logging service.
     */
    errorComponent:
        | JSX.Element
        | ((
              /** The ID of the error that was logged by the error logging service. This parameter will be undefined if the error ID is not yet available in the react lifecycle. */
              errorId?: string,
          ) => JSX.Element);
    sentryDSN?: string;
    releaseVersion?: string;
    beforeSend?: BeforeSendType;
}

interface ErrorBoundaryState {
    hasError: boolean;
    errorId?: string;
}

export class ErrorBoundary extends Component<ErrorBoundaryProps, ErrorBoundaryState> {
    constructor(props: ErrorBoundaryProps) {
        super(props);
        this.state = { hasError: false };
    }

    static getDerivedStateFromError() {
        return { hasError: true };
    }

    componentDidMount() {
        const appConfig = getConfig();
        const { sentryDSN, releaseVersion, beforeSend } = this.props;

        const loggingConfig: ErrorLoggingConfiguration = {
            dataSourceName: sentryDSN,
            environment: appConfig?.environment,
            release: releaseVersion,
            beforeSend,
        };

        initializeErrorLogging(loggingConfig);
    }

    componentDidCatch(error: unknown): void {
        const errorId = logUnknownException(error, Severity.Fatal);
        this.setState({ errorId });
    }

    render() {
        const { hasError, errorId } = this.state;
        const { errorComponent, children } = this.props;

        if (hasError) {
            return typeof errorComponent === 'function' ? errorComponent(errorId) : errorComponent;
        }

        return children;
    }
}
